import { useCallback, useMemo, useState } from 'react';
import useDebouncedCallback from './useDebouncedCallback';
import {
  getDateParameters,
  getDateRange,
  tempFilter,
  clearTimelineTempFilter,
  setTimebarButtonActive,
  resizeViewer,
} from 'utils/builder/IScheduleLogic';

function buildFilterSchema(parameterName, date) {
  return {
    [parameterName]: {
      columnKey: parameterName,
      columnType: 'date',
      conditions: [{ type: 'dateIsBefore', dataArray: [date] }],
    },
  };
}

const isInvalidDate = (d) => !(d instanceof Date) || Number.isNaN(d.getTime());

export default function useTimeline() {
  const [pickerState, setPickerState] = useState({ open: false, params: [], selected: null });
  const [barState, setBarState] = useState({ open: false, min: null, max: null, value: null, paramName: null, locked: false });

  const runTempFilter = useCallback(async (paramName, date) => {
    const schema = buildFilterSchema(paramName, date);
    const result = await tempFilter(schema);
    try {
      window.dispatchEvent(new CustomEvent('bs:timeline-quickcounter', { detail: result?.quickCounterData || [] }));
    } catch {}
  }, []);

  const debouncedRunTempFilter = useDebouncedCallback(runTempFilter, 150);

  const openPicker = useCallback(async () => {
    try {
      const params = await getDateParameters();
      setPickerState({ open: true, params: Array.isArray(params) ? params : [], selected: null });
    } catch {
      setPickerState({ open: true, params: [], selected: null });
    }
  }, []);

  const closeAll = useCallback(async () => {
    setPickerState({ open: false, params: [], selected: null });
    setBarState({ open: false, min: null, max: null, value: null, paramName: null, locked: false });
    // Important: keep Autodesk toolbar button state in sync when timeline is closed via "X"
    // (otherwise next open requires double-click due to state mismatch).
    setTimebarButtonActive(false);
    try {
      const result = await clearTimelineTempFilter();
      window.dispatchEvent(new CustomEvent('bs:timeline-quickcounter', { detail: result?.quickCounterData || [] }));
    } catch {}
  }, []);

  const onParamPick = useCallback(async (param) => {
    const paramName = param?.name ?? param?.displayName ?? param?.label ?? null;
    if (!paramName) return setPickerState((s) => ({ ...s, open: false }));

    try {
      const range = await getDateRange(paramName);
      let min = range?.min ? new Date(range.min) : null;
      let max = range?.max ? new Date(range.max) : null;
      let locked = false;

      // Use backend-provided range as source of truth.
      // If range is invalid, fall back to last 12 months.
      if (isInvalidDate(min) || isInvalidDate(max)) {
        const now = new Date();
        max = now;
        min = new Date(now);
        min.setMonth(now.getMonth() - 12);
      } else if (+min === +max) {
        // Backend returned a single date: lock the timeline (no dragging),
        // and just show the marker centered on that date.
        locked = true;
      }

      // Normalize min and max to start of day for consistent day-based comparisons
      if (min) {
        min.setHours(0, 0, 0, 0);
      }
      if (max) {
        max.setHours(0, 0, 0, 0);
      }

      // Start at min to allow stepping through all days in the range
      const start = new Date(min);

      setPickerState((s) => ({ ...s, open: false, selected: param }));
      setBarState({ open: true, min, max, value: start, paramName, locked });

      await runTempFilter(paramName, start);
    } catch {
      setPickerState((s) => ({ ...s, open: false }));
    }

    resizeViewer();
  }, [runTempFilter]);

  const onDateChange = useCallback((date) => {
    setBarState((s) => {
      if (s.locked) return s;
      const next = { ...s, value: date };
      if (next.paramName) debouncedRunTempFilter(next.paramName, date);
      return next;
    });
  }, [debouncedRunTempFilter]);

  const onCloseBar = useCallback(() => {
    closeAll();
  }, [closeAll]);

  return useMemo(() => ({
    pickerState,
    barState,
    openPicker,
    onParamPick,
    onDateChange,
    onCloseBar,
    closeAll,
  }), [pickerState, barState, openPicker, onParamPick, onDateChange, onCloseBar, closeAll]);
}